import {DataTableSortStatus} from 'mantine-datatable';
import {useTableSort} from '@flexinet/ui-components';
import {useUsersInfiniteQuery} from './useUsersQuery';
import {useUsersSearch} from './useUsersSearch';
import {useUsersFilter} from './useUsersFilter';
import {useUsersTableColumns} from './useUsersTableColumns';
import {User} from '../types';

// users-api's email filter is an EXACT Cognito match (`email = "..."`), so a
// partial address finds nobody. Only a complete address goes server-side, where
// it finds the person even on a page not loaded yet.
const isCompleteEmail = (value: string) => /^\S+@\S+\.\S+$/.test(value.trim());

const matchesKeyword = (item: User, needle: string) => {
    if (!needle) {
        return true;
    }
    return [item.name, item.givenName, item.familyName, item.email, item.username]
        .some(value => typeof value === 'string' && value.toLowerCase().includes(needle));
};

// Newest first. `created` is Cognito's UserCreateDate, set on every user (see
// types.ts). ListUsers returns users in no documented order, so the default is
// applied here rather than trusted from the server.
const DEFAULT_SORT: DataTableSortStatus<User> = {columnAccessor: 'created', direction: 'desc'};

export const useUsers = () => {
    const {filters} = useUsersFilter();
    const {keyword} = useUsersSearch();
    const {columns} = useUsersTableColumns();

    // `status` is not in users-api's filter, so it is matched here.
    const {status, ...serverFilters} = filters;
    const queryFilters = isCompleteEmail(keyword)
        ? {...serverFilters, email: keyword.trim()}
        : serverFilters;

    const query = useUsersInfiniteQuery(queryFilters);

    const items: User[] = (query.data?.pages ?? []).flatMap(page => page?.items ?? []).filter(Boolean);

    const needle = keyword.trim().toLowerCase();

    // Keyword, status and sort all work over the pages LOADED SO FAR. They cannot
    // reach rows the browser has not fetched: that is a limit of token paging,
    // and the footer says how much is loaded.
    const matching = items.filter(item =>
        matchesKeyword(item, needle) && (!status || item.status === status));

    const {sortStatus, setSortStatus, sorted} = useTableSort(matching, DEFAULT_SORT);

    return {
        columns,
        items,
        visibleItems: sorted,
        sortStatus,
        setSortStatus,
        // isPending, not isLoading: a query still waiting to start (auth resolving)
        // has no data yet, and showing it as an empty table claims nobody exists.
        loading: query.isPending,
        isError: query.isError,
        nextToken: query.hasNextPage ? 'has-more' : null,
        isFetchingMore: query.isFetchingNextPage,
        fetchRecords: () => {
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
    };
};
